import React, { useState, useEffect } from 'react';
import { collection, query, orderBy, limit, getDocs } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import { Cloud, CheckCircle, AlertCircle, Loader, Download, RefreshCw } from 'lucide-react';

function AdminHiraSync() {
    const [logs, setLogs] = useState([]);
    const [running, setRunning] = useState(false);
    const [result, setResult] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => { load(); }, []);

    const load = async () => {
        try {
            const snap = await getDocs(query(collection(db, 'syncLogs'), orderBy('finishedAt', 'desc'), limit(20)));
            setLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (e) {
            console.error('동기화 로그 로드 실패:', e);
        }
    };

    const [progress, setProgress] = useState('');
    const [probeResult, setProbeResult] = useState(null);

    const runCleanup = async () => {
        if (!window.confirm('비치과 데이터를 삭제합니다. 계속할까요?')) return;
        setError(null);
        setResult(null);
        setRunning(true);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const cleanup = httpsCallable(fns, 'hiraCleanupNonDental', { timeout: 540000 });
            const res = await cleanup();
            setResult({ stats: { fetched: res.data?.stats?.scanned || 0, written: res.data?.stats?.kept || 0, errors: 0, deleted: res.data?.stats?.deleted || 0 } });
        } catch (e) {
            console.error('정리 실패:', e);
            setError(e?.message || '정리 실패');
        } finally {
            setRunning(false);
        }
    };

    const runDemoSetup = async () => {
        const sggu = window.prompt('어느 시군구 치과를 데모용 예약가능으로 처리할까요?\n예: 김포시, 강남구, 부산진구', '김포시');
        if (!sggu) return;
        const countStr = window.prompt(`"${sggu}" 치과 몇 곳을 처리할까요?`, '5');
        const count = parseInt(countStr) || 5;
        setError(null);
        setResult(null);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const fn = httpsCallable(fns, 'setDemoReservableHospitals', { timeout: 60000 });
            const res = await fn({ sggu, count, enable: true });
            alert(`데모 처리 완료: ${res.data?.updated || 0}곳에 hasReservation=true 설정됨\n앱에서 검색하면 "예약가능" 배지가 보일 거예요.`);
        } catch (e) {
            setError(e?.message || '데모 설정 실패');
        }
    };

    const runProbe = async () => {
        setProbeResult(null);
        setError(null);
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            const probe = httpsCallable(fns, 'hiraProbeCodes', { timeout: 300000 });
            const res = await probe();
            setProbeResult(res.data?.results || []);
        } catch (e) {
            console.error('진단 실패:', e);
            setError(e?.message || '진단 실패');
        }
    };

    const runSync = async (opts) => {
        if (!window.confirm(opts.dryRun ? '시험 동기화를 실행할까요?' : '전체 동기화를 시작합니다.\n분할 처리되어 약 10~15분 소요됩니다.\n계속할까요?')) return;
        setRunning(true);
        setResult(null);
        setError(null);
        setProgress('');
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            // 클라이언트 callable timeout 540초로 늘림 (기본 60초)
            const sync = httpsCallable(fns, 'hiraSyncDentalHospitals', { timeout: 540000 });

            if (opts.dryRun) {
                const res = await sync({ ...opts, maxPages: 2 });
                setResult(res.data);
            } else {
                // 전체: 한 호출당 5페이지(500개)씩 처리 → 안전한 시간 내 완료
                const totals = { fetched: 0, written: 0, errors: 0 };
                const codes = ['51', '41'];
                let totalCalls = 0;
                for (const clCd of codes) {
                    let startPage = 1;
                    let finished = false;
                    let safety = 100;
                    while (!finished && safety-- > 0) {
                        totalCalls++;
                        setProgress(`치과 ${clCd === '51' ? '의원' : '병원'} · 페이지 ${startPage}~${startPage + 1} (호출 ${totalCalls}회 · 누적 ${totals.written}건)`);
                        const res = await sync({ clCd, startPage, maxPages: 2, chunkSize: 100 });
                        const s = res.data?.stats || {};
                        totals.fetched += s.fetched || 0;
                        totals.written += s.written || 0;
                        totals.errors += s.errors || 0;
                        finished = !!s.finished;
                        startPage = (s.lastPage || startPage) + 1;
                    }
                }
                setResult({ stats: totals });
            }
            await load();
        } catch (e) {
            console.error('동기화 실패:', e);
            setError(e?.message || e?.code || '동기화 실패');
        } finally {
            setRunning(false);
            setProgress('');
        }
    };

    return (
        <div style={S.container}>
            <div style={S.header}>
                <h1 style={S.title}><Cloud size={24} style={{ verticalAlign: 'middle', marginRight: 8 }} /> HIRA 의료기관 동기화</h1>
                <p style={S.subtitle}>건강보험심사평가원 OpenAPI에서 전국 치과 정보를 자동 가져옵니다</p>
            </div>

            {/* 시작 카드 */}
            <div style={S.startCard}>
                <h2 style={S.startTitle}>동기화 실행</h2>
                <p style={S.startDesc}>
                    전국 치과의원·치과병원 정보를 <code>hospitals</code> 컬렉션에 저장합니다.<br />
                    기존 데이터의 클레임·구독 정보는 보존됩니다.<br />
                    <strong>예상 소요 시간:</strong> 5~10분 / <strong>예상 결과:</strong> 약 18,000개 치과
                </p>

                <div style={S.btnRow}>
                    <button
                        onClick={runProbe}
                        disabled={running}
                        style={{ ...S.btn, background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}
                    >
                        🔍 분류코드 진단
                    </button>
                    <button
                        onClick={runCleanup}
                        disabled={running}
                        style={{ ...S.btn, background: '#fee2e2', color: '#991b1b', border: '1px solid #fca5a5' }}
                    >
                        🗑 비치과 데이터 정리
                    </button>
                    <button
                        onClick={runDemoSetup}
                        disabled={running}
                        style={{ ...S.btn, background: '#d1fae5', color: '#065f46', border: '1px solid #6ee7b7' }}
                    >
                        🧪 데모: 예약가능 치과 설정
                    </button>
                    <button
                        onClick={() => runSync({ chunkSize: 100, maxPages: 2, dryRun: true })}
                        disabled={running}
                        style={{ ...S.btn, background: '#fff', color: '#0f172a', border: '1px solid #cbd5e1' }}
                    >
                        {running ? <Loader size={14} className="spin" /> : <Download size={14} />} 시험 (2페이지만)
                    </button>
                    <button
                        onClick={() => runSync({ chunkSize: 100, maxPages: 200, dryRun: false })}
                        disabled={running}
                        style={S.btn}
                    >
                        {running ? <><Loader size={14} className="spin" /> 동기화 중...</> : <><RefreshCw size={14} /> 전체 동기화</>}
                    </button>
                </div>

                {probeResult && (
                    <div style={{ marginTop: 14, padding: 12, background: '#f8fafc', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 12, color: '#0f172a' }}>
                        <strong>분류코드 진단 결과</strong>
                        <table style={{ width: '100%', marginTop: 8, borderCollapse: 'collapse', fontSize: 12 }}>
                            <thead>
                                <tr style={{ background: '#e2e8f0' }}>
                                    <th style={{ padding: 6, textAlign: 'left' }}>clCd</th>
                                    <th style={{ padding: 6, textAlign: 'left' }}>분류명</th>
                                    <th style={{ padding: 6, textAlign: 'right' }}>총건수</th>
                                    <th style={{ padding: 6, textAlign: 'left' }}>샘플 기관명</th>
                                </tr>
                            </thead>
                            <tbody>
                                {probeResult.map((r, i) => {
                                    const isDental = (r.sampleClCdNm || '').includes('치과');
                                    return (
                                        <tr key={i} style={{ borderBottom: '1px solid #e2e8f0', background: isDental ? '#d1fae5' : 'transparent' }}>
                                            <td style={{ padding: 6, fontWeight: 700 }}>{r.clCd}</td>
                                            <td style={{ padding: 6 }}>{r.sampleClCdNm || r.error || '-'}</td>
                                            <td style={{ padding: 6, textAlign: 'right' }}>{r.total != null ? r.total.toLocaleString() : '-'}</td>
                                            <td style={{ padding: 6, color: '#64748b' }}>{r.sampleName || '-'}</td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <p style={{ marginTop: 8, color: '#64748b' }}>초록색 행이 치과 분류 코드입니다. 이 코드로 동기화 코드를 수정해야 합니다.</p>
                    </div>
                )}

                {running && (
                    <div style={S.progress}>
                        ⏳ {progress || '동기화 진행 중...'}<br />
                        <span style={{ fontSize: 11, opacity: 0.7 }}>페이지를 닫지 마세요. 완료까지 10~15분 소요됩니다.</span>
                    </div>
                )}

                {result && (
                    <div style={S.success}>
                        <CheckCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        <strong>동기화 완료</strong><br />
                        총 {result.stats?.fetched || 0}건 조회 · 저장 {result.stats?.written || 0}건 · 오류 {result.stats?.errors || 0}건
                    </div>
                )}

                {error && (
                    <div style={S.error}>
                        <AlertCircle size={16} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                        {error}
                    </div>
                )}
            </div>

            {/* 사전 준비 안내 */}
            <div style={S.guideCard}>
                <h3 style={S.guideTitle}>📋 사전 준비 사항</h3>
                <ol style={S.guideList}>
                    <li>공공데이터포털 (data.go.kr) 가입·로그인</li>
                    <li>"건강보험심사평가원_병의원_정보_조회_서비스" 활용 신청</li>
                    <li>승인 후 일반 인증키(Decoded) 복사</li>
                    <li>터미널에서 Firebase Secret 등록:<br />
                        <code style={S.code}>firebase functions:secrets:set HIRA_API_KEY</code>
                    </li>
                    <li>Functions 재배포:<br />
                        <code style={S.code}>firebase deploy --only functions:hiraSyncDentalHospitals,functions:hiraScheduledSync</code>
                    </li>
                </ol>
            </div>

            {/* 동기화 로그 */}
            <div style={S.logsCard}>
                <h3 style={S.logsTitle}>최근 동기화 로그 (최대 20건)</h3>
                {logs.length === 0 ? (
                    <p style={S.empty}>아직 실행된 동기화가 없습니다</p>
                ) : (
                    <div style={S.logsList}>
                        {logs.map(log => (
                            <div key={log.id} style={S.logItem}>
                                <div style={S.logTime}>{formatDate(log.finishedAt)}</div>
                                <div style={S.logStats}>
                                    조회 {log.stats?.fetched || 0}건 · 신규 {log.stats?.created || 0}건 · 갱신 {log.stats?.updated || 0}건
                                    {log.stats?.errors > 0 && <span style={{ color: '#ef4444' }}> · 오류 {log.stats.errors}건</span>}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}

const formatDate = (ts) => {
    if (!ts) return '-';
    const d = ts?.toDate ? ts.toDate() : new Date(ts);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
};

const S = {
    container: { padding: 24, maxWidth: 960, margin: '0 auto', fontFamily: "'Apple SD Gothic Neo', sans-serif" },
    header: { marginBottom: 24 },
    title: { fontSize: 24, fontWeight: 900, color: '#0f172a', margin: 0 },
    subtitle: { fontSize: 14, color: '#64748b', marginTop: 4 },
    startCard: { background: '#fff', borderRadius: 14, padding: 24, marginBottom: 20, border: '1px solid #e2e8f0' },
    startTitle: { fontSize: 18, fontWeight: 800, marginBottom: 8 },
    startDesc: { fontSize: 13, color: '#475569', lineHeight: 1.7, marginBottom: 18 },
    btnRow: { display: 'flex', gap: 8, flexWrap: 'wrap' },
    btn: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 700, cursor: 'pointer' },
    progress: { marginTop: 14, padding: 12, background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' },
    success: { marginTop: 14, padding: 12, background: '#d1fae5', border: '1px solid #6ee7b7', borderRadius: 8, fontSize: 13, color: '#065f46', lineHeight: 1.7 },
    error: { marginTop: 14, padding: 12, background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, fontSize: 13, color: '#991b1b' },
    guideCard: { background: '#f0f9ff', borderRadius: 14, padding: 20, marginBottom: 20, border: '1px solid #bae6fd' },
    guideTitle: { fontSize: 15, fontWeight: 800, color: '#0c4a6e', marginBottom: 10 },
    guideList: { paddingLeft: 24, fontSize: 13, color: '#0c4a6e', lineHeight: 2 },
    code: { background: '#fff', padding: '2px 6px', borderRadius: 4, fontSize: 12, fontFamily: 'monospace', color: '#6366f1' },
    logsCard: { background: '#fff', borderRadius: 14, padding: 20, border: '1px solid #e2e8f0' },
    logsTitle: { fontSize: 15, fontWeight: 800, marginBottom: 12 },
    empty: { fontSize: 13, color: '#94a3b8', padding: '20px 0' },
    logsList: { display: 'flex', flexDirection: 'column', gap: 8 },
    logItem: { padding: 10, background: '#f8fafc', borderRadius: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    logTime: { fontSize: 12, color: '#64748b' },
    logStats: { fontSize: 13, fontWeight: 600, color: '#0f172a' },
};

export default AdminHiraSync;
